import React from 'react';
import {
  CircleIcon,
  HStack,
  Pressable,
  Radio,
  RadioGroup,
  RadioIcon,
  RadioIndicator,
  RadioLabel,
  Text,
  VStack,
} from '@gluestack-ui/themed';
import { useTranslation } from 'react-i18next';

import type { ScheduledChangeSwapRequestReasonCode } from '@/common/api/models/scheduledChangeSwapRequestReasonCode';

const REASON_LEFT: ScheduledChangeSwapRequestReasonCode[] = [
  'wrong_or_not_taught',
  'unclear',
];

const REASON_RIGHT: ScheduledChangeSwapRequestReasonCode[] = [
  'not_my_exam',
  'other',
];

function ReasonRadio({
  code,
  label,
  onSelect,
}: {
  code: ScheduledChangeSwapRequestReasonCode;
  label: string;
  onSelect: (code: ScheduledChangeSwapRequestReasonCode) => void;
}) {
  return (
    <Pressable
      onPress={() => onSelect(code)}
      accessibilityRole="radio"
      testID={`scheduled-swap-reason-${code}`}
    >
      <Radio value={code} size="sm" alignItems="flex-start" pointerEvents="none">
        <HStack space="sm" alignItems="center" flex={1}>
          <RadioIndicator mr="$0" w="$3.5" h="$3.5" flexShrink={0}>
            <RadioIcon as={CircleIcon} size="xs" />
          </RadioIndicator>
          <RadioLabel size="sm" color="$textBody" mb="$0">
            {label}
          </RadioLabel>
        </HStack>
      </Radio>
    </Pressable>
  );
}

type Props = {
  selectedReason?: ScheduledChangeSwapRequestReasonCode;
  onSelectReason: (code: ScheduledChangeSwapRequestReasonCode) => void;
  showResourceReason?: boolean;
};

export function SwapReasonPicker({
  selectedReason,
  onSelectReason,
  showResourceReason = false,
}: Props) {
  const { t } = useTranslation();

  return (
    <VStack space="sm">
      <Text size="sm" fontFamily="$heading" color="$textHeading">
        {t('lecturer.scheduledChanges.swap.why')}
      </Text>
      <RadioGroup
        value={selectedReason ?? ''}
        onChange={(value) => onSelectReason(value as ScheduledChangeSwapRequestReasonCode)}
      >
        <HStack space="md" alignItems="flex-start">
          <VStack flex={1} space="sm">
            {REASON_LEFT.map((code) => (
              <ReasonRadio
                key={code}
                code={code}
                label={t(`lecturer.scheduledChanges.swap.reason.${code}`)}
                onSelect={onSelectReason}
              />
            ))}
          </VStack>
          <VStack flex={1} space="sm">
            {REASON_RIGHT.map((code) => (
              <ReasonRadio
                key={code}
                code={code}
                label={t(`lecturer.scheduledChanges.swap.reason.${code}`)}
                onSelect={onSelectReason}
              />
            ))}
          </VStack>
        </HStack>
        {showResourceReason ? (
          <VStack mt="$2">
            <ReasonRadio
              code="resource_not_exam_relevant"
              label={t('lecturer.scheduledChanges.swap.reason.resource_not_exam_relevant')}
              onSelect={onSelectReason}
            />
          </VStack>
        ) : null}
      </RadioGroup>
    </VStack>
  );
}
